import { Center, Spinner, Text, VStack } from "@chakra-ui/react"
import React, { useCallback, useEffect, useState } from "react"
import { TagBatteryStatus, TagsByFilterQueryVariables, TagStatus, useTagsByFilterQuery } from "../../graphql"
import { TagsList } from "./TagsList"

export type TagsByFilterProps = {
	keyword?: string
	status?: TagStatus
	batteryStatus?: TagBatteryStatus
}

export const TagsByFilter: React.FC<TagsByFilterProps> = ({ keyword, status, batteryStatus }) => {
	const [filter, setFilter] = useState({ keyword, status, batteryStatus })

	const [pagination, setPagination] = useState<TagsByFilterQueryVariables["pagination"]>({ limit: 20, page: 1 })

	const [{ data, fetching, error }] = useTagsByFilterQuery({
		variables: { filter, pagination },
	})

	useEffect(() => {
		const timeoutId = setTimeout(() => {
			setFilter({ keyword, status, batteryStatus })
		}, 1000)

		return () => {
			clearTimeout(timeoutId)
		}
	}, [keyword, status, batteryStatus])

	const nextPage = useCallback(() => {
		if (fetching) return

		if (data?.tagsByFilter.hasNextPage) {
			setPagination((prev) => ({
				...prev,
				page: (prev.page || 0) + 1,
			}))
		}
	}, [data, fetching])

	return (
		<VStack w="full" maxW="2xl" align="stretch">
			{fetching && !data?.tagsByFilter ? (
				<Center w="full" py="4">
					<VStack w="full" color="grayscale.label">
						<Text fontSize="sm">Loading tags</Text>
						<Spinner size="sm" />
					</VStack>
				</Center>
			) : error ? (
				<Center py="4">
					<Text fontSize="sm" fontWeight="semibold" color="error.500">
						{error.message.replace("[GraphQL] ", "")}
					</Text>
				</Center>
			) : data?.tagsByFilter.tags && data.tagsByFilter.tags.length ? (
				<VStack w="full" align="stretch">
					<Text>
						Showing {data.tagsByFilter.tags.length || 0} tag
						{(data.tagsByFilter.tags.length || 0) > 1 ? "s" : ""}
					</Text>

					<TagsList tags={data.tagsByFilter.tags} loadMore={nextPage} />

					{fetching && (
						<Center w="full" py="4">
							<VStack w="full" color="grayscale.label">
								<Text fontSize="sm">Loading more tags</Text>
								<Spinner size="sm" />
							</VStack>
						</Center>
					)}
				</VStack>
			) : (
				<Center py="4">
					<Text fontSize="sm" fontWeight="semibold" color="error.500">
						Couldn&apos;t find any tags.
					</Text>
				</Center>
			)}
		</VStack>
	)
}
